import { useMemo } from "react";
import { formatMoney } from "@/lib/api";

/**
 * Small horizontal strip visualising last N days of journaling activity.
 * Each day is a small cell: green shades = profit day, red = loss, blue = no positions,
 * grey outline = day not journaled.
 */
export function ActivityStrip({ activity = [], currency = "USD" }) {
  const stats = useMemo(() => {
    const journaled = activity.filter((d) => !d.empty).length;
    const green = activity.filter((d) => !d.empty && !d.no_positions && d.total_pnl > 0).length;
    const red = activity.filter((d) => !d.empty && !d.no_positions && d.total_pnl < 0).length;
    const flat = activity.filter((d) => !d.empty && !d.no_positions && d.total_pnl === 0 && d.trade_count > 0).length;
    const noPos = activity.filter((d) => d.no_positions).length;
    return { journaled, green, red, flat, noPos };
  }, [activity]);

  if (!activity.length) return null;

  return (
    <section data-testid="activity-strip" className="mt-8 border border-[#2E2A25] bg-[#1A1815] p-5 rounded-sm">
      <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A]">— Discipline</p>
          <h2 className="font-head text-xl text-[#EBE5D9] mt-1">Last 90 days</h2>
        </div>
        <div className="flex gap-5 font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
          <span><span className="text-[#EBE5D9]">{stats.journaled}</span> journaled</span>
          <span><span className="text-[#7A9E80]">{stats.green}</span> green</span>
          <span><span className="text-[#B56258]">{stats.red}</span> red</span>
          <span><span className="text-[#5C7C8A]">{stats.noPos}</span> flat / no pos</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-[3px]">
        {activity.map((d) => {
          const iso = d.date;
          let cls = "border-[#2E2A25]";
          let bg = "bg-transparent";
          if (!d.empty) {
            if (d.no_positions) { cls = "border-[#5C7C8A]/40"; bg = "bg-[#5C7C8A]/10"; }
            else if (d.total_pnl > 0) { cls = "border-[#5E7A62]/60"; bg = "bg-[#5E7A62]/25"; }
            else if (d.total_pnl < 0) { cls = "border-[#8C4A42]/60"; bg = "bg-[#8C4A42]/25"; }
            else { cls = "border-[#5C7C8A]/50"; bg = "bg-transparent"; }
          }
          const title = d.empty
            ? `${iso} — no entry`
            : d.no_positions
              ? `${iso} — no positions`
              : `${iso} — ${formatMoney(d.total_pnl, currency)} (${d.trade_count} trade${d.trade_count === 1 ? "" : "s"})`;
          return (
            <div
              key={iso}
              data-testid={`activity-cell-${iso}`}
              title={title}
              className={`w-[11px] h-[11px] border rounded-[2px] ${cls} ${bg} hover:scale-125 transition-transform cursor-help`}
            />
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-4 text-[10px] font-mono text-[#A39A8E]">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 border border-[#5E7A62]/60 bg-[#5E7A62]/25 rounded-[2px]" /> profit</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 border border-[#8C4A42]/60 bg-[#8C4A42]/25 rounded-[2px]" /> loss</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 border border-[#5C7C8A]/40 bg-[#5C7C8A]/10 rounded-[2px]" /> no pos</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 border border-[#2E2A25] rounded-[2px]" /> unfilled</span>
      </div>
    </section>
  );
}
